import { useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Chip,
  List,
  ListItemButton,
  ListItemText,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import BusinessIcon from '@mui/icons-material/Business';
import { useAuth } from '../../shared/auth/AuthContext';
import { AuthLayout } from '../components/AuthLayout';
import { roleLabel } from '../../admin/users/components/RoleOptions';

/**
 * Shown after a login by a person who belongs to more than one tenant. Lists their tenants and,
 * on choosing one, exchanges the short-lived selection token for a real tenant-bound session.
 * If there is no pending selection (e.g. the page was reloaded), it bounces back to /login.
 */
export default function TenantSelectPage(): JSX.Element {
  const { pendingTenantSelection, isAuthenticated, tenants, selectTenant } = useAuth();
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);
  const [busyTenantId, setBusyTenantId] = useState<string | null>(null);

  // No pending selection. Completing a selection clears the token *and* signs the user in, so an
  // authenticated caller goes home (not back to /login — that race would bounce a successful pick).
  // Otherwise the token is genuinely gone (e.g. the page was reloaded), so return to /login.
  if (!pendingTenantSelection) {
    return <Navigate to={isAuthenticated ? '/' : '/login'} replace />;
  }

  const choose = async (tenantId: string): Promise<void> => {
    setError(null);
    setBusyTenantId(tenantId);
    try {
      const profile = await selectTenant(tenantId);
      navigate(profile.mustCompleteProfile ? '/profile' : '/', { replace: true });
    } catch {
      setError('Could not open that tenant. Please try again.');
      setBusyTenantId(null);
    }
  };

  return (
    <AuthLayout title="Choose a tenant">
      <Paper sx={{ p: 4, width: '100%', maxWidth: 460 }} elevation={2}>
        <Stack spacing={2}>
          <Box>
            <Typography variant="h5" gutterBottom>
              Choose a tenant
            </Typography>
            <Typography variant="body2" color="text.secondary">
              You belong to more than one tenant. Pick the one you want to work in — you can switch
              later from the account menu.
            </Typography>
          </Box>

          {error && <Alert severity="error">{error}</Alert>}

          <List disablePadding aria-label="Your tenants">
            {tenants.map((tenant) => (
              <ListItemButton
                key={tenant.tenantId}
                onClick={() => void choose(tenant.tenantId)}
                disabled={busyTenantId !== null}
                selected={busyTenantId === tenant.tenantId}
                sx={{ borderRadius: 1, mb: 1, border: 1, borderColor: 'divider' }}
              >
                <BusinessIcon sx={{ mr: 1.5, color: 'text.secondary' }} />
                <ListItemText
                  primary={tenant.name}
                  secondary={
                    <Stack direction="row" spacing={0.5} flexWrap="wrap" sx={{ mt: 0.5 }}>
                      {tenant.roles.map((role) => (
                        <Chip key={role} label={roleLabel(role)} size="small" variant="outlined" />
                      ))}
                    </Stack>
                  }
                  secondaryTypographyProps={{ component: 'div' }}
                />
              </ListItemButton>
            ))}
          </List>
        </Stack>
      </Paper>
    </AuthLayout>
  );
}
